/* Импорт урока из GetCourse: расширение Chrome «MUIP: импорт из GetCourse»
   кладёт в буфер обмена JSON (формат GcPayload), админка проверяет его,
   чистит HTML и превращает в блоки урока. Содержимое буфера — недоверенные
   данные: всё валидируется, HTML проходит через белый список тегов. */

import {
  AudioBlockData,
  ButtonBlockData,
  ImageBlockData,
  LessonBlock,
  ParagraphBlockData,
} from '../../../models/courses';
import { parseVideoUrl } from '../components/blocks/videoEmbed';

export const GC_SOURCE = 'muip-getcourse-import';
// v1 — первая версия расширения (текст, картинки, видео, файлы); v2 — плюс
// аудио, кнопки, ссылки у картинок и список нераспознанного.
export const GC_VERSION = 2;
const SUPPORTED_VERSIONS = [1, 2];

export type GcBlock =
  | { type: 'heading'; level: number; text: string }
  | { type: 'paragraph'; html: string }
  | { type: 'image'; src: string; alt?: string; caption?: string; link?: string }
  | { type: 'video'; url: string; hosting?: 'getcourse' | 'external' }
  | { type: 'audio'; src: string; title?: string }
  | { type: 'button'; text: string; url: string }
  | { type: 'file'; url: string; name: string };

export interface GcPayload {
  source: typeof GC_SOURCE;
  version: number;
  url?: string;
  title: string;
  blocks: GcBlock[];
  assignment: { html: string } | null;
  warnings: string[];
}

export type AssignmentAnswerType = 'free_text' | 'file_upload';

export interface ImportStats {
  headings: number;
  paragraphs: number;
  images: number;
  videos: number;
  videosMissing: number;
  audios: number;
  buttons: number;
  files: number;
  skipped: number;
}

export interface ImportResult {
  title: string;
  sourceUrl: string;
  blocks: LessonBlock[];
  assignmentText: string;
  // Предложенный тип ответа на ДЗ (админ может поменять в диалоге).
  assignmentAnswer: AssignmentAnswerType;
  stats: ImportStats;
  warnings: string[];
}

// Совпадает с белым списком бэкенда (/upload/by-url).
const REHOST_HOSTS = ['getcourse.ru', 'gcfiles.net'];

export const GC_VIDEO_NOTE =
  'Видео с хостинга GetCourse не переносится автоматически — перезалейте его в Kinescope и вставьте ссылку.';

// ---------- валидация ----------

const isObj = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

const isStr = (v: unknown): v is string => typeof v === 'string';

/** Разбирает текст из буфера. Бросает Error с понятным сообщением. */
export function parseGetcoursePayload(raw: string): GcPayload {
  let data: unknown;
  try {
    data = JSON.parse(raw.trim());
  } catch {
    throw new Error(
      'В буфере не данные урока. Нажмите «Скопировать в MUIP» на странице урока GetCourse.',
    );
  }
  if (!isObj(data) || data.source !== GC_SOURCE) {
    throw new Error(
      'В буфере не данные урока. Нажмите «Скопировать в MUIP» на странице урока GetCourse.',
    );
  }
  if (typeof data.version !== 'number' || !SUPPORTED_VERSIONS.includes(data.version)) {
    throw new Error('Версия расширения не поддерживается — обновите расширение.');
  }
  if (!isStr(data.title) || !Array.isArray(data.blocks)) {
    throw new Error('Данные урока повреждены.');
  }

  const blocks: GcBlock[] = [];
  for (const b of data.blocks) {
    if (!isObj(b)) {
      continue;
    }
    switch (b.type) {
      case 'heading':
        if (isStr(b.text)) {
          blocks.push({
            type: 'heading',
            text: b.text,
            level: typeof b.level === 'number' ? b.level : 2,
          });
        }
        break;
      case 'paragraph':
        if (isStr(b.html)) {
          blocks.push({ type: 'paragraph', html: b.html });
        }
        break;
      case 'image':
        if (isStr(b.src)) {
          blocks.push({
            type: 'image',
            src: b.src,
            alt: isStr(b.alt) ? b.alt : '',
            caption: isStr(b.caption) ? b.caption : '',
            link: isStr(b.link) ? b.link : '',
          });
        }
        break;
      case 'audio':
        if (isStr(b.src)) {
          blocks.push({ type: 'audio', src: b.src, title: isStr(b.title) ? b.title : '' });
        }
        break;
      case 'button':
        if (isStr(b.text) && isStr(b.url)) {
          blocks.push({ type: 'button', text: b.text, url: b.url });
        }
        break;
      case 'video':
        if (isStr(b.url)) {
          blocks.push({
            type: 'video',
            url: b.url,
            hosting: b.hosting === 'getcourse' ? 'getcourse' : 'external',
          });
        }
        break;
      case 'file':
        if (isStr(b.url) && isStr(b.name)) {
          blocks.push({ type: 'file', url: b.url, name: b.name });
        }
        break;
      default:
        break;
    }
  }

  const assignment =
    isObj(data.assignment) && isStr(data.assignment.html)
      ? { html: data.assignment.html }
      : null;

  const warnings = Array.isArray(data.warnings)
    ? data.warnings.filter(isStr).slice(0, 50).map((w) => w.slice(0, 200))
    : [];

  return {
    source: GC_SOURCE,
    version: data.version,
    url: isStr(data.url) ? data.url : undefined,
    title: data.title,
    blocks,
    assignment,
    warnings,
  };
}

// ---------- очистка HTML ----------

const ALLOWED_TAGS = new Set([
  'P', 'BR', 'STRONG', 'B', 'EM', 'I', 'U', 'S', 'A', 'UL', 'OL', 'LI', 'BLOCKQUOTE',
]);
// Выкидываются вместе с содержимым.
const DROP_TAGS = new Set([
  'SCRIPT', 'STYLE', 'IFRAME', 'OBJECT', 'EMBED', 'TEMPLATE', 'NOSCRIPT', 'SVG',
  'FORM', 'INPUT', 'BUTTON', 'TEXTAREA', 'SELECT', 'IMG', 'VIDEO', 'AUDIO', 'CANVAS',
  'LINK', 'META',
]);
// Блочные контейнеры превращаются в абзацы.
const BLOCK_AS_P = new Set(['DIV', 'SECTION', 'ARTICLE', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6']);

export function isSafeHref(href: string): boolean {
  try {
    const u = new URL(href);
    return ['http:', 'https:', 'mailto:', 'tel:'].includes(u.protocol);
  } catch {
    return false;
  }
}

function cleanNode(node: Node, doc: Document): Node[] {
  if (node.nodeType === Node.TEXT_NODE) {
    return [doc.createTextNode(node.textContent ?? '')];
  }
  if (node.nodeType !== Node.ELEMENT_NODE) {
    return [];
  }
  const el = node as Element;
  const tag = el.tagName.toUpperCase();
  if (DROP_TAGS.has(tag)) {
    return [];
  }

  const children = Array.from(el.childNodes).flatMap((c) => cleanNode(c, doc));

  if (BLOCK_AS_P.has(tag)) {
    const p = doc.createElement('p');
    children.forEach((c) => p.appendChild(c));
    return [p];
  }
  if (!ALLOWED_TAGS.has(tag)) {
    return children; // span, font и т.п. — оставляем только содержимое
  }

  const out = doc.createElement(tag.toLowerCase());
  if (tag === 'A') {
    const href = el.getAttribute('href') ?? '';
    if (!isSafeHref(href)) {
      return children;
    }
    out.setAttribute('href', href);
    out.setAttribute('target', '_blank');
    out.setAttribute('rel', 'noopener noreferrer');
  }
  children.forEach((c) => out.appendChild(c));
  return [out];
}

const isBlankText = (s: string) => s.replace(/[\s\u00a0]/g, '') === '';

/** Оставляет только безопасное форматирование, понятное Quill. Пустые абзацы убираются. */
export function sanitizeHtml(html: string): string {
  const doc = new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html');
  const out = doc.createElement('div');
  Array.from(doc.body.childNodes)
    .flatMap((n) => cleanNode(n, doc))
    .forEach((n) => out.appendChild(n));

  // Голый текст/инлайн на верхнем уровне заворачиваем в <p>.
  let loose: HTMLParagraphElement | null = null;
  Array.from(out.childNodes).forEach((n) => {
    const isBlock =
      n.nodeType === Node.ELEMENT_NODE &&
      ['P', 'UL', 'OL', 'BLOCKQUOTE'].includes((n as Element).tagName);
    if (isBlock) {
      loose = null;
      return;
    }
    if (!loose) {
      loose = doc.createElement('p');
      out.insertBefore(loose, n);
    }
    loose.appendChild(n);
  });

  Array.from(out.querySelectorAll('p, li, blockquote')).forEach((el) => {
    if (isBlankText(el.textContent ?? '')) {
      el.remove();
    }
  });
  Array.from(out.querySelectorAll('ul, ol')).forEach((el) => {
    if (!el.querySelector('li')) {
      el.remove();
    }
  });

  return out.innerHTML.trim();
}

/** HTML → простой текст (абзацы — переводы строк). Для описания ДЗ. */
export function htmlToText(html: string): string {
  const doc = new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html');
  doc.querySelectorAll('br').forEach((br) => br.replaceWith('\n'));
  doc.querySelectorAll('p, div, li, h1, h2, h3, h4, h5, h6').forEach((el) => {
    el.append('\n');
  });
  return (doc.body.textContent ?? '')
    .replace(/\u00a0/g, ' ')
    .split('\n')
    .map((l) => l.replace(/[ \t]+/g, ' ').trim())
    .join('\n')
    .replace(/\n{2,}/g, '\n')
    .trim();
}

// ---------- конвертация ----------

/** https-ссылка на GetCourse (сайт школы или файловое хранилище). */
export function isGetcourseUrl(src: string): boolean {
  try {
    const u = new URL(src);
    if (u.protocol !== 'https:') {
      return false;
    }
    const host = u.hostname.toLowerCase();
    return REHOST_HOSTS.some((h) => host === h || host.endsWith('.' + h));
  } catch {
    return false;
  }
}

/** Картинку/аудио с GetCourse перекачиваем к себе. */
export const isRehostableImage = isGetcourseUrl;

/** Файл из хранилища GetCourse (PDF, docx…) — его можно перекачать к себе. */
export function isGetcourseFileUrl(url: string): boolean {
  if (!isGetcourseUrl(url)) {
    return false;
  }
  return /\/fileservice\//i.test(new URL(url).pathname);
}

const isHttpsUrl = (s: string): boolean => {
  try {
    return new URL(s).protocol === 'https:';
  } catch {
    return false;
  }
};

const isWebUrl = (s: string): boolean => {
  try {
    return ['https:', 'http:'].includes(new URL(s).protocol);
  } catch {
    return false;
  }
};

const FILE_ANSWER_RE = /прикреп|загруз|файл|скриншот|фото|документ/i;

export function convertPayload(
  payload: GcPayload,
  genId: () => string,
): ImportResult {
  const blocks: LessonBlock[] = [];
  const warnings: string[] = [];
  const stats: ImportStats = {
    headings: 0,
    paragraphs: 0,
    images: 0,
    videos: 0,
    videosMissing: 0,
    audios: 0,
    buttons: 0,
    files: 0,
    skipped: 0,
  };

  // Ссылки на страницы GetCourse (не файлы) после отключения GetCourse умрут.
  const deadLinks = new Set<string>();
  const checkLink = (url: string) => {
    if (isGetcourseUrl(url) && !isGetcourseFileUrl(url)) {
      deadLinks.add(url);
    }
  };

  for (const b of payload.blocks) {
    switch (b.type) {
      case 'heading': {
        const text = b.text.replace(/\s+/g, ' ').trim();
        if (!text) {
          stats.skipped++;
          break;
        }
        const level = Math.min(6, Math.max(1, Math.round(b.level) || 2));
        blocks.push({ id: genId(), type: 'heading', data: { level, text } });
        stats.headings++;
        break;
      }
      case 'paragraph': {
        const text = sanitizeHtml(b.html);
        if (!text) {
          stats.skipped++;
          break;
        }
        blocks.push({ id: genId(), type: 'paragraph', data: { text } });
        stats.paragraphs++;
        break;
      }
      case 'image': {
        if (!isHttpsUrl(b.src)) {
          stats.skipped++;
          warnings.push(`Картинка пропущена (не https): ${b.src.slice(0, 80)}`);
          break;
        }
        const data: ImageBlockData = { src: b.src, alt: b.alt ?? '', caption: b.caption ?? '' };
        if (b.link && isWebUrl(b.link)) {
          data.link = b.link;
          checkLink(b.link);
        }
        blocks.push({ id: genId(), type: 'image', data });
        stats.images++;
        break;
      }
      case 'video': {
        const parsed = parseVideoUrl(b.url);
        if (parsed && b.hosting !== 'getcourse') {
          blocks.push({
            id: genId(),
            type: 'video',
            data: { provider: parsed.provider, video_id: parsed.id, caption: '' },
          });
          stats.videos++;
        } else {
          blocks.push({
            id: genId(),
            type: 'video',
            data: {
              provider: 'kinescope',
              video_id: '',
              caption: '',
              import_note:
                b.hosting === 'getcourse'
                  ? GC_VIDEO_NOTE
                  : `Видео не распознано (${b.url.slice(0, 80)}) — вставьте ссылку Kinescope или YouTube.`,
            },
          });
          stats.videosMissing++;
        }
        break;
      }
      case 'audio': {
        if (!isHttpsUrl(b.src)) {
          stats.skipped++;
          warnings.push(`Аудио пропущено (не https): ${b.src.slice(0, 80)}`);
          break;
        }
        blocks.push({
          id: genId(),
          type: 'audio',
          data: { src: b.src, caption: (b.title ?? '').trim() },
        });
        stats.audios++;
        break;
      }
      case 'button':
      case 'file': {
        const url = b.url;
        const text = (b.type === 'button' ? b.text : b.name).trim() || 'Открыть файл';
        if (!isWebUrl(url)) {
          stats.skipped++;
          warnings.push(`Кнопка «${text}» пропущена: неподдерживаемая ссылка`);
          break;
        }
        checkLink(url);
        blocks.push({ id: genId(), type: 'button', data: { text, url } });
        if (isGetcourseFileUrl(url)) {
          stats.files++;
        } else {
          stats.buttons++;
        }
        break;
      }
      default:
        break;
    }
  }

  if (stats.videosMissing > 0) {
    warnings.unshift(
      `Видео без ссылки: ${stats.videosMissing}. Блоки созданы пустыми и помечены ⚠️ — перезалейте видео и вставьте ссылку.`,
    );
  }
  deadLinks.forEach((url) =>
    warnings.push(
      `Ссылка ведёт на страницу GetCourse и перестанет работать после отключения: ${url.slice(0, 100)}`,
    ),
  );
  payload.warnings.forEach((w) => warnings.push(`Не перенесено: ${w}`));

  const assignmentText = payload.assignment ? htmlToText(payload.assignment.html) : '';

  return {
    title: payload.title.trim(),
    sourceUrl: payload.url ?? '',
    blocks,
    assignmentText,
    assignmentAnswer: FILE_ANSWER_RE.test(assignmentText) ? 'file_upload' : 'free_text',
    stats,
    warnings,
  };
}

// ---------- перенос медиа с GetCourse ----------

export type RehostKind = 'image' | 'audio' | 'file';

/** Что перекачать к себе: url из поля field блока blockId. */
export interface RehostTask {
  blockId: string;
  kind: RehostKind;
  field: 'src' | 'link' | 'url' | 'html';
  url: string;
}

function paragraphFileLinks(html: string): string[] {
  const doc = new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html');
  const urls = Array.from(doc.querySelectorAll('a[href]'))
    .map((a) => a.getAttribute('href') ?? '')
    .filter(isGetcourseFileUrl);
  return Array.from(new Set(urls));
}

/** Все медиа и файлы GetCourse в блоках, которые нужно перекачать в наше хранилище. */
export function collectRehostTasks(blocks: LessonBlock[]): RehostTask[] {
  const tasks: RehostTask[] = [];
  blocks.forEach((b) => {
    if (b.type === 'image') {
      const d = b.data as ImageBlockData;
      if (isGetcourseUrl(d.src)) {
        tasks.push({ blockId: b.id, kind: 'image', field: 'src', url: d.src });
      }
      if (d.link && isGetcourseFileUrl(d.link)) {
        tasks.push({ blockId: b.id, kind: 'file', field: 'link', url: d.link });
      }
    } else if (b.type === 'audio') {
      const d = b.data as AudioBlockData;
      if (isGetcourseUrl(d.src)) {
        tasks.push({ blockId: b.id, kind: 'audio', field: 'src', url: d.src });
      }
    } else if (b.type === 'button') {
      const d = b.data as ButtonBlockData;
      if (isGetcourseFileUrl(d.url)) {
        tasks.push({ blockId: b.id, kind: 'file', field: 'url', url: d.url });
      }
    } else if (b.type === 'paragraph') {
      paragraphFileLinks((b.data as ParagraphBlockData).text).forEach((url) =>
        tasks.push({ blockId: b.id, kind: 'file', field: 'html', url }),
      );
    }
  });
  return tasks;
}

/** Подменяет перекачанную ссылку в блоке (возвращает новый блок). */
export function applyRehost(block: LessonBlock, task: RehostTask, newUrl: string): LessonBlock {
  if (block.id !== task.blockId) {
    return block;
  }
  if (task.field === 'html') {
    const text = (block.data as ParagraphBlockData).text;
    const doc = new DOMParser().parseFromString(`<body>${text}</body>`, 'text/html');
    doc.querySelectorAll('a[href]').forEach((a) => {
      if (a.getAttribute('href') === task.url) {
        a.setAttribute('href', newUrl);
      }
    });
    return { ...block, data: { text: doc.body.innerHTML } };
  }
  const data = block.data as unknown as Record<string, unknown>;
  if (data[task.field] !== task.url) {
    return block; // админ уже поменял поле вручную — не перетираем
  }
  return { ...block, data: { ...block.data, [task.field]: newUrl } as LessonBlock['data'] };
}
